import { useEffect, useMemo } from 'react';

import LocalBranchMap from 'components/LocalBranchMap';
import FormTitle from 'components/PageLayout/FormTitle';
import HowToMakePayments from 'components/curo-components/common/HowToMakePayments';
import Table from 'components/curo-components/common/Table';
import Title from 'components/curo-components/common/Title';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useGetBranchGeocode from 'hooks/useGetBranchGeocode';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';

import styles from './EndPages.module.scss';
import {
  formatLoanOfferDataToTableData,
  getTableRows,
  parseLoanOffersToLoanOfferItem,
  parseRefinanceLoanOffersToLoanOfferItem,
} from './constants';

const getRefinancePageTitle = (borrowerFirstName: string) => (
  <>
    Congrats, {borrowerFirstName}!
    <br />
    Your new refinance loan is now open!
  </>
);
const HARD_INQUIRY_PAGE_TITLE = 'Congrats! Your loan is now open!';
const getRefinancePageDescription = (borrowerEmail: string) => (
  <p>
    Your new loan takes effect immediately and your old loan is considered paid off. Any added funds
    will be deposited into your bank account within one business day. Your local branch will give
    you a call in the coming days to answer any questions.
    <br />
    <br />
    You will find a summary about your loan below. A copy of your signed loan agreement has been
    sent to your email at {borrowerEmail}.
  </p>
);
const getHardInquiryPageDescription = (borrowerEmail: string) => (
  <p>
    Your funds should be deposited into your bank account provided within one business day. Your
    local branch will give you a call in the coming days to welcome you and answer any questions.
    <br />
    <br />
    You will find a summary about your loan below. A copy of your signed loan agreement has been
    sent to your email at {borrowerEmail}.
  </p>
);

const CongratsPage = () => {
  const application = useAppSelector(getApplicationSelector)!;
  const { branchPhone, branchStreet, branchCityStateZip, latLng } = useGetBranchGeocode();
  const trackEvent = useGoogleAnalytics();

  const isRefinance = application?.variables[
    CuroApplicationVariables.SmallLoanRefiJourney
  ] as boolean;
  const isAutoPay = application?.variables[CuroApplicationVariables.AutoPay];
  const borrowerEmail = application?.variables[CuroApplicationVariables.BorrowerEmail] as string;
  const borrowerFirstName = application?.variables[
    CuroApplicationVariables.BorrowerFirstName
  ] as string;

  useEffect(() => {
    if (isRefinance) {
      trackEvent(GoogleAnalyticsEventType.ApplyProcessFundingRefi);
    } else {
      trackEvent(GoogleAnalyticsEventType.ApplyProcessFundingNewLoan);
    }
  }, []);

  const loanOffers = useMemo(() => {
    if (isRefinance) {
      return parseRefinanceLoanOffersToLoanOfferItem(application.variables);
    }

    return parseLoanOffersToLoanOfferItem(application.variables);
  }, []);

  const getPageTitle = () => {
    return isRefinance ? getRefinancePageTitle(borrowerFirstName) : HARD_INQUIRY_PAGE_TITLE;
  };

  const getPageDescription = () => {
    return isRefinance
      ? getRefinancePageDescription(borrowerEmail)
      : getHardInquiryPageDescription(borrowerEmail);
  };

  const renderContent = () => {
    const loanOffersEntries = Object.entries(loanOffers);

    const [, loanOfferDetails] = loanOffersEntries?.[0];

    const { loanOfferData } = loanOfferDetails;

    const tableData = formatLoanOfferDataToTableData({
      offerData: loanOfferData,
      origGpsLoanOrAccountNumber: application.variables[
        CuroApplicationVariables.OrigGpsLoanOrAccountNumber
      ] as string,
    });

    return (
      <div className={styles.congratsContent}>
        <div className={styles.congratsTable}>
          <Title type="h5" className={styles.tableTitle}>
            Loan Summary
          </Title>
          <Table data={tableData} rows={getTableRows(isRefinance)} columns={1} />
        </div>

        <div>
          {latLng && (
            <LocalBranchMap
              lat={latLng.lat}
              lng={latLng.lng}
              branchCityStateZip={branchCityStateZip}
              branchPhone={branchPhone}
              branchStreet={branchStreet}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <FormTitle title={getPageTitle()} />
      <div className={styles.congratsSubtitle}>{getPageDescription()}</div>
      {renderContent()}
      {!isAutoPay && <HowToMakePayments />}
    </>
  );
};

export default CongratsPage;
